/**
 * Estilos das telas de Compras (§10.5): ordens e necessidades.
 *
 * Reaproveita os tokens que já existem — `--sp-*`, `--fs-*` e o `theme.colors`
 * dos outros componentes — e segue o mesmo desenho de
 * `styles/components/reports/RelatorioComum.ts`: bloco de contexto, selo,
 * filtros, painel explicativo. Não introduz tema novo e não toca em
 * `styles/global/*`.
 *
 * O `Selo` ganha um tom a mais que o dos relatórios (`ok`), porque aqui existe
 * um estado que é bom notícia — a ordem aprovada — e pintá-lo de azul
 * informativo o deixaria indistinguível de "aguardando".
 */
import styled from "styled-components";

export const Bloco = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--sp-8);
  padding: var(--sp-8) var(--sp-10);
  margin-bottom: var(--sp-10);
  border: 1px solid ${(props) => props.theme.colors.text}33;
  border-radius: 6px;
  background-color: #ffffffcc;
  font-size: var(--fs-sm);
  color: ${(props) => props.theme.colors.text};
`;

export const Icone = styled.span`
  font-family: "Material Icons";
  font-size: var(--fs-lg);
  line-height: 1;
`;

export const Texto = styled.span`
  line-height: 1.45;
`;

export const Forte = styled.strong`
  font-weight: 600;
`;

export const Selo = styled.span<{ tom?: "ok" | "atencao" | "info" | "erro" }>`
  display: inline-flex;
  align-items: center;
  gap: var(--sp-4);
  padding: 2px var(--sp-8);
  border-radius: 12px;
  font-size: var(--fs-xs);
  font-weight: 600;
  white-space: nowrap;
  color: ${(props) =>
    props.tom === "erro"
      ? "#be3028"
      : props.tom === "atencao"
      ? "#8f5e07"
      : props.tom === "ok"
      ? "#26792f"
      : "#1c7295"};
  background-color: ${(props) =>
    props.tom === "erro"
      ? "#be302814"
      : props.tom === "atencao"
      ? "#8f5e0714"
      : props.tom === "ok"
      ? "#26792f14"
      : "#1c729514"};
  border: 1px solid
    ${(props) =>
      props.tom === "erro"
        ? "#be302840"
        : props.tom === "atencao"
        ? "#8f5e0740"
        : props.tom === "ok"
        ? "#26792f40"
        : "#1c729540"};
`;

export const Cabecalho = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--sp-10);
  margin-bottom: var(--sp-10);
`;

export const Titulo = styled.h2`
  margin: 0;
  font-size: var(--fs-xl);
  color: ${(props) => props.theme.colors.text};
`;

export const Subtitulo = styled.p`
  margin: var(--sp-4) 0 0 0;
  font-size: var(--fs-sm);
  color: ${(props) => props.theme.colors.text}aa;
  line-height: 1.5;
`;

export const Filtros = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: var(--sp-10);
  padding: var(--sp-10);
  margin-bottom: var(--sp-10);
  border: 1px solid ${(props) => props.theme.colors.text}33;
  border-radius: 6px;
  background-color: #ffffffcc;
`;

export const Campo = styled.label`
  display: flex;
  flex-direction: column;
  gap: var(--sp-4);
  font-size: var(--fs-xs);
  color: ${(props) => props.theme.colors.text};
`;

export const Entrada = styled.input`
  padding: var(--sp-4) var(--sp-8);
  border: 1px solid ${(props) => props.theme.colors.text}55;
  border-radius: 4px;
  font-size: var(--fs-sm);
  background-color: #ffffff;
  color: ${(props) => props.theme.colors.text};
`;

export const Selecao = styled.select`
  padding: var(--sp-4) var(--sp-8);
  border: 1px solid ${(props) => props.theme.colors.text}55;
  border-radius: 4px;
  font-size: var(--fs-sm);
  background-color: #ffffff;
  color: ${(props) => props.theme.colors.text};
  min-width: calc(var(--u) * 40);
`;

export const Botao = styled.button`
  padding: var(--sp-4) var(--sp-16);
  border: 1px solid ${(props) => props.theme.colors.selection};
  border-radius: 4px;
  background-color: ${(props) => props.theme.colors.selection};
  color: ${(props) => props.theme.colors.textOnPrimary};
  font-size: var(--fs-sm);
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  &:hover:not(:disabled) {
    opacity: 0.9;
  }
`;

export const BotaoSecundario = styled(Botao)`
  background-color: transparent;
  color: ${(props) => props.theme.colors.text};
  border-color: ${(props) => props.theme.colors.text}55;
`;

export const BotaoPerigo = styled(Botao)`
  background-color: transparent;
  color: #be3028;
  border-color: #be302855;
`;

export const Lista = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--sp-8);
`;

export const CartaoDaOrdem = styled.button<{ selecionado?: boolean }>`
  display: block;
  width: 100%;
  text-align: left;
  padding: var(--sp-10);
  border: 1px solid
    ${(props) =>
      props.selecionado
        ? props.theme.colors.selection
        : `${props.theme.colors.text}22`};
  border-radius: 8px;
  background-color: #ffffffcc;
  cursor: pointer;
  color: ${(props) => props.theme.colors.text};
  box-shadow: 0 1px 3px rgba(18, 32, 26, 0.1);
`;

export const LinhaDoCartao = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--sp-8);
  font-size: var(--fs-sm);

  & + & {
    margin-top: var(--sp-4);
  }
`;

export const Codigo = styled.span`
  font-weight: 700;
  font-size: var(--fs-md);
`;

export const Tabela = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: var(--fs-sm);
`;

export const Cabecalhos = styled.th<{ $numerica?: boolean }>`
  text-align: ${(props) => (props.$numerica ? "right" : "left")};
  padding: var(--sp-4) var(--sp-8);
  border-bottom: 1px solid ${(props) => props.theme.colors.text}33;
  font-size: var(--fs-xs);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${(props) => props.theme.colors.text}aa;
  white-space: nowrap;
`;

export const Celula = styled.td<{ $numerica?: boolean; $alerta?: boolean }>`
  text-align: ${(props) => (props.$numerica ? "right" : "left")};
  padding: var(--sp-4) var(--sp-8);
  border-bottom: 1px solid ${(props) => props.theme.colors.text}14;
  color: ${(props) => (props.$alerta ? "#8f5e07" : "inherit")};
  vertical-align: top;
`;

export const Rodape = styled.tr`
  font-weight: 700;

  td {
    border-top: 2px solid ${(props) => props.theme.colors.text}33;
    border-bottom: none;
    padding-top: var(--sp-8);
  }
`;

export const Painel = styled.div<{ tom?: "atencao" | "info" | "erro" }>`
  padding: var(--sp-16);
  margin-bottom: var(--sp-10);
  border-radius: 8px;
  line-height: 1.55;
  border: 1px solid
    ${(props) =>
      props.tom === "erro"
        ? "#be302840"
        : props.tom === "atencao"
        ? "#8f5e0740"
        : "#1c729540"};
  background-color: ${(props) =>
    props.tom === "erro"
      ? "#be302808"
      : props.tom === "atencao"
      ? "#8f5e0708"
      : "#1c729508"};
  color: ${(props) => props.theme.colors.text};
`;

export const TituloDoPainel = styled.h3`
  margin: 0 0 var(--sp-8) 0;
  font-size: var(--fs-md);
`;

export const ListaDeMotivos = styled.ul`
  margin: 0 0 0 var(--sp-20);
  padding: 0;
  line-height: 1.6;
`;

export const Vazio = styled.div`
  padding: var(--sp-20);
  text-align: center;
  color: ${(props) => props.theme.colors.text}99;
  font-size: var(--fs-sm);
  line-height: 1.6;
`;

export const TextoExato = styled.pre`
  margin: var(--sp-8) 0 0 0;
  padding: var(--sp-10);
  border: 1px dashed ${(props) => props.theme.colors.text}44;
  border-radius: 6px;
  background-color: #f7f8f7;
  font-size: var(--fs-xs);
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
  max-height: calc(var(--u) * 160);
  overflow: auto;
`;

export const Paginacao = styled.div`
  display: flex;
  align-items: center;
  gap: var(--sp-10);
  margin-top: var(--sp-10);
  font-size: var(--fs-sm);
  color: ${(props) => props.theme.colors.text}aa;
`;
